import { isCancelledError, QueryClient, QueryKey } from '@tanstack/react-query';
import { Entry } from 'contentful';

import { contentfulClient } from '@/contentful/api/client.contentful';
import { IGeneral, IGeneralFields } from '@/contentful/types';

import { LanguageKey } from '@models/Language';

import { TranslationMap } from './localization.store.types';

export const TRANSLATION_QUERY_KEY_PREFIX = ['translations'] satisfies QueryKey;

export const createTranslationQueryKey = (languageKey: LanguageKey) =>
  [...TRANSLATION_QUERY_KEY_PREFIX, languageKey] satisfies QueryKey;

export const createTranslationMap = (translations: Entry<IGeneralFields>[]) =>
  translations.reduce<Record<string, string | undefined>>(
    (translationMap, { fields: { key, value } }) => ({
      ...translationMap,
      ...(key && { [key]: value }),
    }),
    {}
  );

export const fetchTranslations = async (languageKey: LanguageKey) => {
  const items = await contentfulClient.getEntries<IGeneral>({
    contentType: 'general',
    locale: languageKey,
    select: 'fields.key,fields.value',
    batchSize: 1000,
  });

  return createTranslationMap(items);
};

export const queryTranslations = async (
  languageKey: LanguageKey,
  queryClient: QueryClient
): ReturnType<typeof fetchTranslations> => {
  try {
    const translations = await queryClient.fetchQuery(
      createTranslationQueryKey(languageKey),
      () => fetchTranslations(languageKey)
    );

    return translations;
  } catch (e) {
    // If query was cancelled, try again. This can happen if query cache is
    // cleared during fetching, for example when logging out.
    if (isCancelledError(e)) {
      return queryTranslations(languageKey, queryClient);
    }

    throw e;
  }
};

export const prefetchTranslations = (
  languageKey: LanguageKey,
  queryClient: QueryClient
) =>
  queryClient.prefetchQuery(createTranslationQueryKey(languageKey), () =>
    fetchTranslations(languageKey)
  );

export const getTranslationQueryData = (
  languageKey: LanguageKey,
  queryClient: QueryClient
) =>
  queryClient.getQueryData<TranslationMap[LanguageKey]>(
    createTranslationQueryKey(languageKey)
  ) ?? null;
